import { EvaCardComponent, EvaCardData, EvaCardOptions } from '../eva-card/eva-card.component';
import { GridOptions } from '../grid/grid.component';
import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  Inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  SimpleChanges,
  Type,
  ViewChildren,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { SliderOptions } from '../slider/slider/slider.component';
import { TagTextData } from '../tag-text/tag-text.component';
import { Device } from 'projects/common/model/Device';
import { TextData } from '../text/text.component';
import { ButtonOptions } from '../eva-button/eva-button.component';
import { Module, TextAlign } from 'projects/common/model/ModuleModel';
import { ScreenType } from 'projects/common/model/ScreenType';
import { DOCUMENT, isPlatformServer } from "@angular/common";
import { ElementRef, ViewChild, HostBinding, HostListener } from '@angular/core';
import { MediaPictureService } from 'projects/common/services/media-picture.service';
import { SliderProOptions } from '../slider-pro/slider-pro.component';
import { AbstractComponent } from '../abstract.component';


export interface FooterOptions {
  line_bottom: boolean;
  centered: boolean;
}

export interface Footer {
  button_options: ButtonOptions;
  options: FooterOptions;
}

export interface EvaCardsWrapperModule extends Module<EvaCardsWrapperData> {
  pretitle: TagTextData;
  title: TagTextData;
  subtitle: TagTextData;
  text_horizontal_align: TextAlign;
}

export interface EvaCardsWrapperOptions {
  invert_row_order?: boolean;
  title_horizontal_align?: TextAlign;
  ld_json?: {
    name: string;
    description: string;
  };
}

export interface EvaCardsWrapperData {
  slider_options: SliderOptions;
  grid_options: GridOptions;
  eva_cards: EvaCardData[];
  eva_card_options: EvaCardOptions;
  side_title: TagTextData;
  side_text: TagTextData;
  side_pretitle: TagTextData;
  button_options: ButtonOptions;
  nofollow: boolean;
  footer: Footer;
  options?: EvaCardsWrapperOptions;
}

@Component({
  selector: 'eva-cards-wrapper',
  template: `
    <div *ngIf="module.pretitle || module.title || module.subtitle || (module.logo && module.logo.src)" class="-eva-3-mb-xlg title-container {{module.data.options?.title_horizontal_align}}" [ngClass]="{'-with-logo': module.logo && module.logo.src}">
      <img *ngIf="module.logo && module.logo.src && mediaPictureService.getMediaUrl(module.logo.src) as src" class="logo" [src]="src" loading="lazy" alt="logo" />
      <text *ngIf="module.pretitle || module.title || module.subtitle" [data]="module" class=""></text>
    </div>

    <div class="wrapper {{wrapperClass}}" #containerRef>
      <text *ngIf="module.data.side_text || module.data.side_title || module.data.side_pretitle"
            class="{{textClass}} side-text -eva-3-mb-xlg"
            [ngClass]="{slider: !!module.data.slider_options, grid: !!module.data.grid_options}"
            [moduleId]="'EvaCardsWrapperComponent'" [moduleIndex]="module.row_number"
            [data]="textData" [showButton]="device.type.toString() !== 'MOBILE'"></text>

      <eva-cards-slider-wrapper *ngIf="module.data.slider_options" [data]="module.data" [flow]="flow"
                                [device]="device" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id"
                              [moduleId]="module.id" [widthContainer]="widthContainer"></eva-cards-slider-wrapper>

      <eva-cards-grid-wrapper *ngIf="module.data.grid_options" [data]="module.data" [flow]="flow" [moduleIndex]="module.row_number"
                              [uniqueId]="module.unique_id" [cardsPerView]="module.data.grid_options.columns"></eva-cards-grid-wrapper>
    </div>

    <div *ngIf="module.data.footer" class="footer-module" [ngClass]="{
        '-line-bottom': module.data.footer.options && module.data.footer.options.line_bottom,
        '-centered': module.data.footer.options && module.data.footer.options.centered}">
      <eva-button *ngIf="module.data.footer.button_options" class="button-container"
                  [options]="module.data.footer.button_options"
                  [moduleId]="'EvaCardsWrapperComponent'" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id"
                  [nofollow]="module.data.nofollow"></eva-button>
    </div>
  `,
  styleUrls: ['./eva-cards-wrapper.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EvaCardsWrapperComponent extends AbstractComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() override module: EvaCardsWrapperModule;
  @Input() device: Device;
  @Input() override flow: string;

  textData: TextData;
  wrapperClass: string;
  textClass: string;
  override screenType: ScreenType;
  widthContainer: number;

  @ViewChild('containerRef', { static: true }) containerRef: ElementRef;

  @HostBinding('style.--wrapper-side-text') wrapperSideText = 330;

  constructor(injector: Injector,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    public mediaPictureService: MediaPictureService,
    private renderer: Renderer2) {
    super(injector);
    this.screenType = this.landingModelService.getLandingModel().device.type;
  }

  ngOnInit() {
    this.onResizeContainer();
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.module?.firstChange && changes?.module?.currentValue != null) {
      this.init();
    }
  }

  init() {
    this.textData = {
      text_data: this.module.data.side_text,
      title: this.module.data.side_title,
      pretitle: this.module.data.side_pretitle,
      button_options: this.module.data.button_options,
      text_horizontal_align: this.module.text_horizontal_align
    };
    this.updateClasses();
    if (this.module.data.options?.ld_json?.name && isPlatformServer(this.platformId)) {
      this.addLdJson();
    }
  }

  updateClasses() {
    this.wrapperClass = this.module.data.options?.invert_row_order ? 'reversed-row' : '';
    this.textClass = this.screenType === ScreenType.DESKTOP ? `-eva-3-p${this.module.data.options?.invert_row_order ? 'l' : 'r'}-xxlg` : '';
  }

  addLdJson() {
    const ldJson = {
      '@context': ['https://schema.org', { csvw: 'http://www.w3.org/ns/csvw#' }],
      '@type': 'Dataset',
      name: this.module.data.options?.ld_json?.name,
      description: this.module.data.options?.ld_json?.description,
      mainEntity:
      {
        '@type': 'csvw:Table',
        'csvw:tableSchema': {
          'csvw:columns': [...this.module.data.eva_cards.map(card => ({
            'csvw:name': card.title?.text,
            'csvw:datatype': 'string',
            'csvw:cells': [{
              'csvw:value': card.text?.text
            }]
          }))
          ]
        }
      }
    };
    const script = this.renderer.createElement('script');
    script.id = `${this.module.unique_id}-ld+json`;
    script.innerHTML = JSON.stringify(ldJson, null, 2);
    script.type = 'application/ld+json';
    this.renderer.appendChild(this.document.head, script);
  }

  ngAfterViewInit() {
    setTimeout(() => this.onResizeContainer(), 0);
  }

  getModuleId() {
    return this.module?.id;
  }

  @HostListener('window:resize') onResizeContainer() {
    if (this.containerRef) {
      this.widthContainer = (this.module.data.side_text || this.module.data.side_title || this.module.data.side_pretitle) && this.screenType === ScreenType.DESKTOP ?
        (this.containerRef.nativeElement.clientWidth - this.wrapperSideText) || 950
        : this.containerRef.nativeElement.clientWidth || 1280;
    }
  }
}

@Component({
  selector: 'eva-cards-slider-wrapper',
  template: `
      <slider-pro
        [options]="sliderProOptions"
        [componentParams]="componentParams"
        [components]="components"
        [upaData]="upaData">
      </slider-pro>
  `,
  styleUrls: ['./eva-cards-slider-wrapper.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EvaCardsSliderWrapperComponent implements OnInit, OnChanges {

  @Input() moduleIndex: number;
  @Input() moduleId: string;
  @Input() uniqueId?: string;
  @Input() data: EvaCardsWrapperData;
  @Input() device: Device;
  @Input() flow: string;
  @Input() widthContainer: number;

  sliderOptions: SliderOptions;
  sideText?: TagTextData;
  sliderWidth: number;
  spaceBetween = 24;
  offersPerRow = 4;
  upaData: any;

  componentParams: any[];
  components: Type<any>[];
  sliderProOptions: SliderProOptions;

  @HostBinding('style.--card-size') cardSize = 299;

  constructor() { }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.data?.firstChange && changes?.data?.currentValue != null) {
      this.init();
    }
  }

  init() {
    this.upaData = {
      moduleId: this.moduleId,
      moduleIndex: this.moduleIndex,
      uniqueId: this.uniqueId
    };

    this.transformModelToSliderPro();

    let cardsLength = this.data.eva_cards.filter(card => card.title?.text || card.text?.text || card.card_image?.image || card.card_image?.images_list).length;
    if (this.device.type.toString() === 'MOBILE' && this.data.button_options) {
      cardsLength++;
    }

    this.sliderOptions = { ...this.data.slider_options, ...{ items_length: cardsLength } };
    this.sideText = this.sliderOptions.side_text;
  }

  transformModelToSliderPro() {
    this.components = new Array(this.data.eva_cards.length).fill(EvaCardComponent);
    this.componentParams = this.data.eva_cards.map(evaCard => {
      return {
        options: this.data.eva_card_options || {},
        data: evaCard,
        items_to_show: this.data.slider_options.items_to_show,
        moduleIndex: this.moduleIndex,
        positionIndex: 0,
        nofollow: this.data.nofollow,
        flow: this.flow,
        viewType: 'slide',
        uniqueId: this.uniqueId
      }
    });

    if (this.device.type.toString() === 'MOBILE' && this.data.button_options) {
      this.components.push(SlideItemButton);
      this.componentParams.push({
        buttonOptions: this.data.button_options,
        moduleIndex: this.moduleIndex,
        moduleId: this.moduleId,
        uniqueId: this.uniqueId
      });
    }

    this.sliderProOptions = {
      itemsToShow: { 'DEFAULT': this.device.type == 'MOBILE' ? 1.1 : this.data.slider_options.items_to_show },
      itemsToMove: { 'DEFAULT': this.data.slider_options.items_to_move },
      dragToMove: { 'DEFAULT': this.data.slider_options.deactivate_touch || this.device.type != 'DESKTOP' },
      showArrows: { 'DEFAULT': this.data.slider_options.show_arrows },
      showDots: { 'DEFAULT': true },
      dotsLighten: this.data.slider_options.lighten,
      arrowsInnerContainer: this.data.slider_options.arrows_inner_container,
      arrowsSize: this.data.slider_options.arrows_size
    };
  }
}

@Component({
  selector: 'eva-cards-grid-wrapper',
  template: `
    <grid [options]='data.grid_options'>
      <eva-card #gridEvaCard *ngFor="let evaCard of data.eva_cards; let i = index" class="eva-card-{{i}}"
                [options]="data.eva_card_options"
                [data]="evaCard" [itemsToShow]="data.grid_options.columns"
                [moduleIndex]="moduleIndex" [uniqueId]="uniqueId"
                [positionIndex]="i" [nofollow]="data.nofollow" [flow]="flow"
                [cardWidth]="cardWidth" viewType='grid' 
                [ngStyle]="!!data.grid_options.grid_template_areas?{'grid-area': 'item-' + (i+1)}:{}">
      </eva-card>
    </grid>
  `,
  encapsulation: ViewEncapsulation.None
})
export class EvaCardsGridWrapperComponent implements AfterContentChecked {
  @ViewChildren('gridEvaCard', { read: ViewContainerRef }) gridEvaCard: QueryList<ViewContainerRef>;

  @Input() moduleIndex: number;
  @Input() uniqueId?: string;
  @Input() data: EvaCardsWrapperData;
  @Input() cardsPerView: number;
  @Input() flow: string;

  cardWidth: number;

  ngAfterContentChecked() {
    if (!this.cardWidth) {
      this.cardWidth = this.gridEvaCard?.first?.element?.nativeElement?.offsetWidth;
    }
  }
}

@Component({
  selector: 'slide-item-button',
  template: `
    <a [href]="buttonOptions.link" target="_blank"
      class="eva-card eva-3-card -eva-3-shadow-line eva-3-link see-more-card">
      <eva-button class="-eva-3-mb-sm" [newTab]="true"
        [options]="{circle: true, type: '-darken', ghost_button: true, size: '-lg', next: true, link: buttonOptions.link}"
        [moduleId]="moduleId" [moduleIndex]="moduleIndex" [uniqueId]="uniqueId">
      </eva-button>
      <span class="-eva-3-mt-sm">{{buttonOptions.link_text}}</span>
    </a>
  `
})
export class SlideItemButton {
  @Input() buttonOptions: ButtonOptions;
  @Input() moduleIndex: number;
  @Input() moduleId: string;
  @Input() uniqueId: string;
}
